import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate.ts";
import { useEffect, useState } from "react";
import { CircleDollarSign, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { GD } from "src/GD";
import { AccountingCard as Card, KpiLabel } from "./styled";
import { useRouter } from "src/services/Router";
import { formatProfitAmount, getAccountantProfitRequest, parseAccountantProfit, profitSuccessRate, sumProfitMetrics, type AccountantProfitData, type ProfitBreakdownRow } from "./accountantData";
import styled from "styled-components";
import { AdaptiveTabBar } from "../conponents/AdaptiveTabBar";

const Kpis = styled.dl`
	display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); margin: 18px 0 0;
	border: 1px solid #e7ebf1; border-radius: 8px; overflow: hidden;
	> div { min-width: 0; padding: 14px 16px; border-right: 1px solid #edf0f4; }
	> div:last-child { border-right: 0; }
	dt { color: #667085; font-size: 12px; line-height: 1.4; }
	dd { margin: 5px 0 0; color: #1d2939; font-size: 20px; font-weight: 600; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
	dd.positive { color: #157347; }
	@media (max-width: 700px) { grid-template-columns: repeat(2, minmax(0, 1fr)); > div { border-bottom: 1px solid #edf0f4; } }
`;

const TableScroll = styled.div`
	max-width: 100%; overflow-x: auto; margin-top: 12px; border: 1px solid #e7ebf1; border-radius: 8px;
	table { width: 100%; min-width: 930px; border-collapse: collapse; font-size: 13px; }
	th, td { padding: 10px 12px; border-bottom: 1px solid #edf0f4; text-align: right; white-space: nowrap; }
	th { background: #fafbfc; color: #667085; font-size: 12px; font-weight: 500; }
	th:first-child, td:first-child { text-align: left; }
	td { color: #475467; font-variant-numeric: tabular-nums; }
	td:first-child { color: #1d2939; font-weight: 500; white-space: normal; min-width: 190px; }
	tbody tr:last-child td { border-bottom: 0; }
	tbody tr:hover { background: #f8fafc; }
	tbody tr[data-selected="true"] { background: #f0f5ff; }
	tfoot td { background: #f5f7fa; border-top: 2px solid #d0d5dd; border-bottom: 0; color: #1d2939; font-weight: 600; }
	.company-name { display: flex; align-items: center; gap: 10px; }
	input[type="checkbox"] { width: 16px; height: 16px; margin: 0; flex-shrink: 0; cursor: pointer; accent-color: #365f8c; }
	input:focus-visible { outline: 2px solid #6374d9; outline-offset: 2px; }
	a { color: #365f8c; text-decoration: none; }
	a:hover { text-decoration: underline; }
	a:focus-visible { outline: 2px solid #6374d9; outline-offset: 2px; }
`;

const Notes = styled.div`
	margin-top: 14px; padding: 10px 12px; border-left: 3px solid #d99a00; background: #fff9ed; color: #6f4b00; font-size: 12px; line-height: 1.5;
`;

interface AccountantProfitCardProps {
	projectId: number;
	userId: number;
	from: string;
	to: string;
	currency: string;
}

export const AccountantProfitCard = ({ projectId, userId, from, to, currency }: AccountantProfitCardProps) => {
	useLocale();
	const { t } = useTranslation();
	const router = useRouter();
	const [tab, setTab] = useState<"clients" | "channels">("clients");
	const [selectedClients, setSelectedClients] = useState<Set<string>>(new Set());
	const [state, setState] = useState<{ busy: boolean; data?: AccountantProfitData; error?: string }>({ busy: true });

	useEffect(() => {
		let cancelled = false;
		const subscription = GD.S_AUTH_LOG_OUT.subscribe(() => { cancelled = true; setState({ busy: false }); });
		setState({ busy: true });
		setSelectedClients(new Set());
		const load = async () => {
			const checkContext = async () => {
				const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
				if (cancelled || profile?.id !== userId || project?.id !== projectId) throw new Error("Context changed");
			};
			try {
				await checkContext();
				const response = await GD.REQ_API.request(getAccountantProfitRequest(projectId, from, to, currency));
				await checkContext();
				if (response?.error || response?.data === undefined || response.data === null) throw new Error(msg("profitRequestFailed"));
				const data = parseAccountantProfit(response.data, from, to, currency);
				if (!data) throw new Error(msg("unexpectedProfitResponse"));
				if (!cancelled) setState({ busy: false, data });
			} catch {
				if (!cancelled) setState({ busy: false, error: t("accountant.profitError") });
			}
		};
		void load();
		return () => { cancelled = true; GD.S_AUTH_LOG_OUT.unsubscribe(subscription); };
	}, [projectId, userId, from, to, currency, t]);
	const locale = t("accountant.locale");
	const count = (value: number) => value.toLocaleString(locale);
	const percent = (row: AccountantProfitData["totals"]) => {
		const value = profitSuccessRate(row);
		return value === null ? "—" : `${value.toLocaleString(locale, { maximumFractionDigits: 2 })}%`;
	};
	const money = (value: number) => formatProfitAmount(value, currency, locale);
	const rows = state.data ? (tab === "clients" ? state.data.clients : state.data.channels) : [];
	const clientKey = (row: ProfitBreakdownRow) => JSON.stringify([row.id, row.name]);
	const selectedRows = (state.data?.clients ?? []).filter(row => selectedClients.has(clientKey(row)));
	const selectedTotals = sumProfitMetrics(selectedRows);
	const toggleClient = (key: string) => setSelectedClients(previous => {
		const next = new Set(previous);
		if (next.has(key)) next.delete(key); else next.add(key);
		return next;
	});
	const renderRows = (items: ProfitBreakdownRow[]) => items.map(row => <tr key={`${tab}-${row.id ?? "unknown"}-${row.name}`} data-selected={tab === "clients" && selectedClients.has(clientKey(row))}>
		<td><div className="company-name">{tab === "clients" && <input type="checkbox" checked={selectedClients.has(clientKey(row))} onChange={() => toggleClient(clientKey(row))} aria-label={t("accountant.selectClient", { name: row.name })} />}{tab === "clients" && row.id !== null ? <a href={`/clients/${row.id}`} onClick={event => {
			if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
			event.preventDefault(); router.navigate("client", { cid: String(row.id) });
		}}>{row.name}</a> : row.name}</div></td>
		<td>{money(row.metrics.turnover)}</td><td>{money(row.metrics.revenue)}</td><td>{money(row.metrics.acquirerCost)}</td><td>{money(row.metrics.profit)}</td>
		<td>{count(row.metrics.countOk)}</td><td>{count(row.metrics.countFailed)}</td><td>{percent(row.metrics)}</td>
	</tr>);

	return <Card style={{ height: "auto", minWidth: 0 }} role="region" aria-label={t("accountant.profitTitle")} aria-busy={state.busy}>
		<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
			<div style={{ padding: 10, borderRadius: 12, color: "#157347", background: "#f0faf4", display: "flex" }}><TrendingUp size={20} aria-hidden="true" /></div>
			<div><div style={{ fontSize: 14, fontWeight: 600, color: "#1a1d26" }}>{t("accountant.profitTitle")}</div><KpiLabel>{from} → {to} · {currency}{state.data ? ` · ${t("accountant.source", { source: state.data.source })}` : ""}</KpiLabel></div>
		</div>
		<div role="status" aria-live="polite">
			{state.busy && <KpiLabel style={{ display: "block", marginTop: 12 }}>{t("accountant.profitLoading")}</KpiLabel>}
			{state.error && <div style={{ marginTop: 12, color: "#b42332", fontSize: 13 }}>{state.error}</div>}
		</div>
		{state.data && <>
			<Kpis>
				<div><dt>{t("accountant.turnover")}</dt><dd>{money(state.data.totals.turnover)}</dd></div>
				<div><dt>{t("accountant.revenue")}</dt><dd>{money(state.data.totals.revenue)}</dd></div>
				<div><dt>{t("accountant.acquirerCost")}</dt><dd>{money(state.data.totals.acquirerCost)}</dd></div>
				<div><dt>{t("accountant.profit")}</dt><dd className="positive">{money(state.data.totals.profit)}</dd></div>
				<div><dt>{t("accountant.successful")}</dt><dd>{count(state.data.totals.countOk)}</dd></div>
				<div><dt>{t("accountant.failed")}</dt><dd>{count(state.data.totals.countFailed)}</dd></div>
				<div><dt>{t("accountant.successRate")}</dt><dd>{percent(state.data.totals)}</dd></div>
			</Kpis>
			<AdaptiveTabBar label={t("accountant.breakdown")} sx={{ marginTop: "18px" }} items={
				(["clients", "channels"] as const).map(kind => ({
					id: kind, label: t(`accountant.${kind}`, { count: state.data![kind].length }),
					selected: tab === kind, onSelect: () => setTab(kind),
				}))
			} />
			{rows.length ? <TableScroll role="tabpanel" aria-label={t(tab === "clients" ? "accountant.clientsTable" : "accountant.channelsTable")}>
				<table><thead><tr><th>{t(tab === "clients" ? "accountant.client" : "accountant.channel")}</th><th>{t("accountant.turnover")}</th><th>{t("accountant.revenue")}</th><th>{t("accountant.acquirerCost")}</th><th>{t("accountant.profit")}</th><th>{t("accountant.successful")}</th><th>{t("accountant.failed")}</th><th>{t("accountant.successRate")}</th></tr></thead>
				<tbody>{renderRows(rows)}</tbody>
				{tab === "clients" && selectedRows.length > 0 && <tfoot aria-live="polite"><tr>
					<td>{t("accountant.selectedTotal", { count: selectedRows.length })}</td>
					<td>{money(selectedTotals.turnover)}</td><td>{money(selectedTotals.revenue)}</td><td>{money(selectedTotals.acquirerCost)}</td><td>{money(selectedTotals.profit)}</td>
					<td>{count(selectedTotals.countOk)}</td><td>{count(selectedTotals.countFailed)}</td><td>{percent(selectedTotals)}</td>
				</tr></tfoot>}</table>
			</TableScroll> : <KpiLabel style={{ display: "block", marginTop: 14 }}>{t("accountant.profitEmpty")}</KpiLabel>}
			{!!state.data.notes.length && <Notes><CircleDollarSign size={15} aria-hidden="true" style={{ verticalAlign: -3, marginRight: 7 }} />{state.data.notes.map(note => /acquirer cost is zero/i.test(note) ? t("accountant.zeroAcquirerCostNote") : note).join(" · ")}</Notes>}
		</>}
	</Card>;
};
